import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { enhancePortraitApi } from '../services/trungTamApi';

export type PortraitVariant = {
  base64: string;
  mimeType: string;
  createdAt: string; // ISO
};

type Props = {
  cccdAvatarBase64: string | null | undefined;
  /** Ảnh đang làm avatar (default = ảnh gốc; user có thể chọn variant AI). */
  portraitBase64: string | null | undefined;
  /** Ảnh gốc — never deleted. */
  portraitOriginalBase64?: string | null | undefined;
  /** JSON string: array of AI variants. All kept across regen. */
  portraitAiVariantsJson?: string | null | undefined;
  faceMatchScore: number | null | undefined;
  /**
   * onChange(portrait, score, original, variantsJson)
   * All 4 passed on every change so the parent can keep the full state.
   */
  onChange: (
    portraitBase64: string | null,
    faceMatchScore: number | null,
    portraitOriginalBase64?: string | null,
    portraitAiVariantsJson?: string | null,
  ) => void;
  disabled?: boolean;
};

const MAX_SIDE = 800;
const JPEG_QUALITY = 0.82;

const fileToResizedBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const longest = Math.max(img.width, img.height);
      const scale = longest > MAX_SIDE ? MAX_SIDE / longest : 1;
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Không thể xử lý ảnh'));
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      const base64 = dataUrl.split(',')[1] || dataUrl;
      resolve(base64);
    };
    img.onerror = () => reject(new Error('Không đọc được ảnh'));
    img.src = reader.result as string;
  };
  reader.onerror = () => reject(new Error('Không đọc được file'));
  reader.readAsDataURL(file);
});

const toSrc = (b64: string | null | undefined): string | null => {
  if (!b64) return null;
  return b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
};

const parseVariants = (json: string | null | undefined): PortraitVariant[] => {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

/**
 * Portrait slot — NO auto AI on upload.
 *
 * Default flow:
 *   1. User uploads selfie → lưu ảnh gốc, avatar = ảnh gốc.
 *   2. User muốn AI thì bấm nút "Tạo AI" → gọi enhance, append result
 *      vào variants gallery.
 *   3. User click vào bất kỳ ảnh nào (gốc hoặc AI variant) → set avatar.
 *   4. TẤT CẢ ảnh AI đã gen đều được giữ — chỉ avatar được thay đổi.
 */
export const PortraitUploader: React.FC<Props> = ({
  portraitBase64,
  portraitOriginalBase64,
  portraitAiVariantsJson,
  onChange,
  disabled,
}) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busyGen, setBusyGen] = useState(false);
  const [busyUpload, setBusyUpload] = useState(false);

  const variants = parseVariants(portraitAiVariantsJson);
  // Legacy records may have only `portraitBase64` (the original-tracking
  // column was added later). Fall back to it as the "original" so the
  // gallery still renders — otherwise the popup wrongly shows an empty
  // "Chọn ảnh chân dung" state for records that DO have a portrait.
  const effectiveOriginal = portraitOriginalBase64 || portraitBase64 || null;
  const hasOriginal = !!effectiveOriginal;

  // Compare "base64 stripped of data: prefix" to know which item is
  // currently the avatar (portraitBase64 may have prefix or not).
  const stripPrefix = (s: string | null | undefined) =>
    s ? (s.startsWith('data:') ? (s.split(',', 2)[1] || s) : s) : '';
  const activeB64 = stripPrefix(portraitBase64);
  const isActive = (b64: string | null | undefined) => !!activeB64 && stripPrefix(b64) === activeB64;

  const handleUpload = useCallback(async (file: File) => {
    setBusyUpload(true);
    try {
      const b64 = await fileToResizedBase64(file);
      // Default avatar = ảnh gốc. AI variants giữ nguyên (nếu có từ
      // trước) — nhưng khi re-upload ảnh gốc mới, variants CŨ vẫn còn
      // ngồi trong gallery. Nếu không muốn giữ AI cũ khi đổi người,
      // pass `null` ở đây. Operator policy: luôn giữ — user tự xóa.
      onChange(b64, null, b64, portraitAiVariantsJson ?? null);
      toast.success('Đã lưu ảnh gốc');
    } catch (err) {
      toast.error((err as { message?: string })?.message || 'Lỗi xử lý ảnh');
    } finally {
      setBusyUpload(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [onChange, portraitAiVariantsJson]);

  const handleGenerateAi = useCallback(async () => {
    const src = effectiveOriginal;
    if (!src) {
      toast.warn('Cần upload ảnh gốc trước');
      return;
    }
    setBusyGen(true);
    try {
      const resp = await enhancePortraitApi({ imageBase64: stripPrefix(src) });
      if (resp.EC === 0 && resp.DT?.enhancedBase64) {
        const newVariant: PortraitVariant = {
          base64: resp.DT.enhancedBase64,
          mimeType: resp.DT.mimeType || 'image/jpeg',
          createdAt: new Date().toISOString(),
        };
        const nextVariants = [...variants, newVariant];
        const nextJson = JSON.stringify(nextVariants);
        // Append variant, avatar giữ nguyên (user tự click nếu muốn đổi).
        // Persist src as portraitOriginalBase64 so future AI gens have a
        // proper original to work from (legacy records may have lacked it).
        onChange(portraitBase64 ?? null, null, src, nextJson);
        toast.success(`Đã tạo ảnh AI #${nextVariants.length} — click vào để chọn làm avatar`);
      } else {
        toast.warn(`AI không tạo được: ${resp.EM || 'unknown'}`);
      }
    } catch (err) {
      const e = err as { response?: { data?: { EM?: string } }; message?: string };
      toast.error(e?.response?.data?.EM || 'Gemini đang bận — thử lại sau');
    } finally {
      setBusyGen(false);
    }
  }, [effectiveOriginal, portraitBase64, variants, onChange]);

  const setAsAvatar = useCallback((b64: string, _label: string) => {
    // Local pick only — parent treats this as a non-persisted change
    // (see TtScannerForm onChange handler that skips scheduleAutoSave
    // when only `portraitBase64` differs). Avatar commits to BE when
    // operator clicks Lưu hồ sơ. No toast — clicking back-and-forth
    // would otherwise spam the corner of the screen.
    onChange(b64, null, effectiveOriginal ?? null, portraitAiVariantsJson ?? null);
  }, [onChange, effectiveOriginal, portraitAiVariantsJson]);

  const handleRemoveVariant = useCallback((idx: number) => {
    if (!window.confirm(`Xóa ảnh AI #${idx + 1}?`)) return;
    const removed = variants[idx];
    const next = variants.filter((_, i) => i !== idx);
    // Nếu xóa variant đang làm avatar → fallback về ảnh gốc (or
    // effectiveOriginal for legacy records that lacked the column).
    const stillAvatar = !isActive(removed.base64);
    const newAvatar = stillAvatar ? portraitBase64 : effectiveOriginal;
    onChange(newAvatar ?? null, null, effectiveOriginal ?? null, next.length ? JSON.stringify(next) : null);
  }, [variants, portraitBase64, effectiveOriginal, onChange, isActive]);

  const handleClearAll = () => {
    if (!window.confirm('Xóa toàn bộ ảnh (ảnh gốc + AI variants)?')) return;
    onChange(null, null, null, null);
  };

  return (
    <div className="tt-portrait">
      <div className="tt-portrait__header">
        <i className="material-icons">photo_camera_front</i>
        <span className="tt-portrait__title">Ảnh thẻ học viên</span>
        {variants.length > 0 && (
          <span className="tt-portrait__ai-badge" title={`${variants.length} ảnh AI đã tạo`}>
            <i className="material-icons">auto_awesome</i>
            {variants.length} AI
          </span>
        )}
      </div>

      {hasOriginal ? (
        <>
          <div className="tt-portrait__gallery">
            {/* Ảnh gốc — falls back to portraitBase64 for legacy records
                that didn't track the original separately. */}
            <button
              type="button"
              className={`tt-portrait__gallery-item ${isActive(effectiveOriginal) ? 'is-active' : ''}`}
              onClick={() => setAsAvatar(stripPrefix(effectiveOriginal)!, 'ảnh gốc')}
              disabled={disabled}
              title="Click để dùng ảnh gốc làm avatar"
            >
              <div className="tt-portrait__gallery-label">
                <i className="material-icons">image</i>
                Gốc
                {isActive(effectiveOriginal) && <span className="tt-portrait__gallery-check">✓</span>}
              </div>
              <img src={toSrc(effectiveOriginal)!} alt="Ảnh gốc" className="tt-portrait__gallery-img" />
            </button>

            {/* Các variant AI — key bằng createdAt + 8 byte đầu base64 để
                React không tái sử dụng DOM node của variant đã xoá khi
                index dồn xuống. Trước dùng `key={i}` khiến sau khi xoá
                AI #2, AI #3 (giờ ở index 1) hiển thị đúng nhưng class
                .is-active không update kịp do node cũ giữ state. */}
            {variants.map((v, i) => (
              <div
                key={`${v.createdAt}-${v.base64.slice(0, 16)}`}
                className={`tt-portrait__gallery-item-wrap ${isActive(v.base64) ? 'is-active' : ''}`}
              >
                <button
                  type="button"
                  className="tt-portrait__gallery-item"
                  onClick={() => setAsAvatar(v.base64, `AI #${i + 1}`)}
                  disabled={disabled}
                  title="Click để dùng ảnh AI này làm avatar"
                >
                  <div className="tt-portrait__gallery-label">
                    <i className="material-icons">auto_awesome</i>
                    AI #{i + 1}
                    {isActive(v.base64) && <span className="tt-portrait__gallery-check">✓</span>}
                  </div>
                  <img src={toSrc(v.base64)!} alt={`AI ${i + 1}`} className="tt-portrait__gallery-img" />
                </button>
                <button
                  type="button"
                  className="tt-portrait__gallery-remove"
                  onClick={() => handleRemoveVariant(i)}
                  disabled={disabled}
                  title="Xóa ảnh AI này"
                >
                  <i className="material-icons">close</i>
                </button>
              </div>
            ))}

            {/* Khung "Đang tạo AI" khi busyGen */}
            {busyGen && (
              <div className="tt-portrait__gallery-item tt-portrait__gallery-loading">
                <span className="tt-spinner" />
                <span style={{ fontSize: 12 }}>AI đang tạo...</span>
                <span style={{ fontSize: 10, color: '#888' }}>~30-60s</span>
              </div>
            )}
          </div>

          <div className="tt-portrait__actions">
            <button
              type="button"
              className="tt-btn tt-btn--primary tt-btn--sm"
              onClick={handleGenerateAi}
              disabled={disabled || busyGen || !hasOriginal}
              title="Tạo 1 ảnh AI mới từ ảnh gốc — giữ lại tất cả ảnh AI cũ"
            >
              <i className="material-icons">auto_awesome</i>
              {busyGen ? 'Đang tạo...' : variants.length > 0 ? `Tạo AI thêm (đã có ${variants.length})` : 'Tạo AI'}
            </button>
            <button
              type="button"
              className="tt-btn tt-btn--outline tt-btn--sm"
              onClick={() => fileRef.current?.click()}
              disabled={disabled || busyUpload}
            >
              <i className="material-icons">refresh</i>
              Thay ảnh gốc
            </button>
            <button
              type="button"
              className="tt-btn tt-btn--outline tt-btn--sm tt-portrait__remove"
              onClick={handleClearAll}
              disabled={disabled}
            >
              <i className="material-icons">delete</i>
              Xóa hết
            </button>
          </div>
        </>
      ) : (
        <button
          type="button"
          className="tt-portrait__dropzone"
          onClick={() => fileRef.current?.click()}
          disabled={disabled || busyUpload}
          title="Chọn ảnh chân dung"
        >
          <i className="material-icons">{busyUpload ? 'sync' : 'add_a_photo'}</i>
          <span className="tt-portrait__dropzone-text">
            {busyUpload ? 'Đang xử lý...' : 'Chọn ảnh chân dung'}
          </span>
          <span className="tt-portrait__dropzone-hint">
            Ảnh gốc lưu trực tiếp. Bấm "Tạo AI" sau để gen ảnh thẻ 3×4.
          </span>
        </button>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleUpload(f);
        }}
        disabled={disabled || busyUpload}
      />
    </div>
  );
};

export default PortraitUploader;
